import { useEffect, useState, type RefObject } from "react";

// The Figma's scroll threshold: smaller moves are a thumb's jitter, not a direction.
const DIRECTION_THRESHOLD_PX = 8;

function isStuck(element: HTMLElement): boolean {
	const stickyTop = parseFloat(getComputedStyle(element).top) || 0;
	return element.getBoundingClientRect().top <= stickyTop + 1;
}

export interface StickyState {
	stuck: boolean;
	hidden: boolean;
}

export function useHideOnScroll(
	ref: RefObject<HTMLElement | null>
): StickyState {
	const [state, setState] = useState<StickyState>({
		stuck: false,
		hidden: false
	});

	useEffect(() => {
		let lastY = window.scrollY;

		function onScroll(): void {
			const element = ref.current;
			if (!element) return;
			const stuck = isStuck(element);
			const y = window.scrollY;
			const delta = y - lastY;
			const turned = Math.abs(delta) >= DIRECTION_THRESHOLD_PX;
			if (turned) lastY = y;
			setState((current) => {
				const hidden = turned ? delta > 0 && stuck : current.hidden;
				return current.stuck === stuck && current.hidden === hidden
					? current
					: { stuck, hidden };
			});
		}

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [ref]);

	return state;
}
